import { client } from "@/modules/integrations/accounts/client";
import type { LeadCreate } from "@/providers/smarta-accounts/accounts-api-client";
import { Lead, LeadPage } from "./types";

export const LEADS_PAGE_SIZE = 20;

const HTTP_CONFLICT = 409;

/**
 * The email already belongs to an active lead. A 409 on `POST /api/leads` has
 * this one meaning, so the form shows its own translated text for it and never
 * the API's (English) message.
 */
export class LeadEmailTakenError extends Error {
  name = "LeadEmailTakenError";
}

/**
 * One page of leads from `GET /api/leads`, paged on the server with
 * `limit` / `offset` (a true item offset, not a page number).
 */
export const listLeads = async (
  page: number,
  pageSize = LEADS_PAGE_SIZE
): Promise<LeadPage> => {
  const { data, error } = await client.GET("/api/leads", {
    params: { query: { limit: pageSize, offset: (page - 1) * pageSize } },
  });
  if (error || !data?.data) {
    throw error || new Error("Malformed list-leads response");
  }
  return { leads: data.data, total: data.pagination.total };
};

/**
 * Create a lead via `POST /api/leads`. A 409 means the email is taken and
 * becomes a `LeadEmailTakenError`; every other failure is thrown as is.
 */
export const createLead = async (payload: LeadCreate): Promise<Lead> => {
  const { data, error, response } = await client.POST("/api/leads", {
    body: payload,
  });
  if (response.status === HTTP_CONFLICT) {
    throw new LeadEmailTakenError("Lead email already exists");
  }
  if (error || !data?.data) {
    throw error || new Error("Malformed create-lead response");
  }
  return data.data;
};

/** Assign an operator via `PUT /api/leads/{id}/assignee`. */
export const setLeadAssignee = async (
  id: string,
  operatorId: string
): Promise<Lead> => {
  const { data, error } = await client.PUT("/api/leads/{id}/assignee", {
    params: { path: { id } },
    body: { operatorId },
  });
  if (error || !data?.data) {
    throw error || new Error("Malformed set-lead-assignee response");
  }
  return data.data;
};

/** Remove the assignee via `DELETE /api/leads/{id}/assignee`. */
export const clearLeadAssignee = async (id: string): Promise<Lead> => {
  const { data, error } = await client.DELETE("/api/leads/{id}/assignee", {
    params: { path: { id } },
  });
  if (error || !data?.data) {
    throw error || new Error("Malformed clear-lead-assignee response");
  }
  return data.data;
};
